import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import { Stack } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import type { FieldConfiguration } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { CustomColumnField, ReportField } from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import { selectListFields } from "../../../../store/selectors/fieldsStateSelectors.ts";
import { fieldsStateActions } from "../../../../store/thunks/fieldStatesThunks.ts";
import { isCustomColumn, isDimensionBased } from "../../../../utilities/fieldsHelper.ts";
import { supportsFormatting } from "../../common/fields/custom-field/utilities/calculationTypeCapabilities.ts";
import DimensionFormattingContainer from "../../common/fields/format/DimensionFormattingContainer.tsx";
import { canApplyFormat } from "../../common/fields/format/dimensionFormattingHelper.tsx";
import MeasureFieldsContainer from "../../common/fields/format/MeasureFieldsContainer.tsx";
import TextFieldContainer from "../../tabular/table/TextFieldsContainer.tsx";

interface Props {
  readonlyMode: boolean;
}

const ListFormatContainer = ({ readonlyMode }: Props) => {
  const dispatch = useAppDispatch();
  const fields = useSelector(selectListFields);

  const dimensions = useMemo(() => fields.filter(isDimensionBased), [fields]);
  const formattableFields = useMemo(() => dimensions.filter(canApplyFormat), [dimensions]);
  const formattableCustomFields = useMemo(
    () =>
      fields.filter(
        (field): field is CustomColumnField => isCustomColumn(field) && supportsFormatting(field.config.calculationType)
      ),
    [fields]
  );

  const handleDimensionChange = useCallback(
    (dimension: ReportField, changes: Partial<FieldConfiguration>) => {
      const field = fields.find((f) => f.config.guid === dimension.config.guid);
      if (isDimensionBased(field)) {
        dispatch(fieldsStateActions.updateFieldConfig({ field, changes }));
      }
    },
    [dispatch, fields]
  );

  const handleCustomFieldChange = useCallback(
    (customField: ReportField, changes: Partial<FieldConfiguration>) => {
      const field = fields.find((f) => f.config.guid === customField.config.guid);
      if (isCustomColumn(field)) {
        dispatch(fieldsStateActions.updateFieldConfig({ field, changes }));
      }
    },
    [dispatch, fields]
  );

  return (
    <ScrollableFlexContainer scrollContainerSx={{ px: "1.3rem" }}>
      <Stack sx={{ gap: 1 }}>
        <TextFieldContainer fields={dimensions} onUpdateConfig={handleDimensionChange} readonlyMode={readonlyMode} />
        <DimensionFormattingContainer
          dimensions={formattableFields}
          onUpdateConfig={handleDimensionChange}
          readonlyMode={readonlyMode}
        />
        <MeasureFieldsContainer
          measures={formattableCustomFields}
          onChange={handleCustomFieldChange}
          readonlyMode={readonlyMode}
        />
      </Stack>
    </ScrollableFlexContainer>
  );
};

export default ListFormatContainer;
